import {
  type AchievementCategory,
  PASSPORT_SECTIONS,
  type Passport,
  type PassportSection,
  type TransferStatus,
  formatTransferCode,
  passportFileName,
  seasonStory,
  transferStatus,
  usePassportPreview,
  usePeople,
  useProfileTransfers,
  useTransferActions,
} from '@shkola/api';
import { subjectLook } from '@shkola/design';
import { DownloadSimpleIcon } from '@phosphor-icons/react/DownloadSimple';
import { KeyIcon } from '@phosphor-icons/react/Key';
import { PrinterIcon } from '@phosphor-icons/react/Printer';
import { type ReactNode, type RefObject, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentContext } from '../app/ViewContext';
import {
  Block,
  EventArt,
  GRAD,
  type Grad,
  Head,
  INK,
  INK_FAINT,
  INK_SOFT,
  LINE,
  PAPER,
  PageHead,
  PillIcon,
  SUBJECT_GRAD,
  SubjectArt,
  grad,
  pillClass,
} from '../design/forms';
import { ArrowUpRightIcon } from '../design/icons';
import { FIELD, Label, Sub } from '../student/LifeArt';
import { KindArt } from './AchievementsPage';

/*
 * «Переїзд без втрат» in «Форми 2» (28.10). The title on the paper, what it is under it. For the parent:
 * on the left what to pass on (each part with a switch), the dark pill for the code and the light ones to
 * print or save; the code itself big on cobalt; the passports made, with their state as labels. On the
 * right the passport as the new school will read it: a white sheet, light headings, the subjects with
 * their pictures, the best grades by hand. For the school: the code to open on the left, the opened
 * passport and the passports of our students on the right.
 */

const longDate = (iso: string) =>
  new Intl.DateTimeFormat('uk-UA', {
    timeZone: 'Europe/Kyiv',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(iso.length === 10 ? `${iso}T12:00:00Z` : iso));

/** Saves the passport as a file — for a school that is not on Parta yet. */
function download(passport: Passport) {
  const blob = new Blob([JSON.stringify(passport, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = passportFileName(passport);
  a.click();
  URL.revokeObjectURL(url);
}

/** The top of a sticky column: under the bar, or higher for a column taller than the window. */
function useStickyTop(ref: RefObject<HTMLDivElement | null>) {
  const [top, setTop] = useState(80);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const fit = () => setTop(Math.min(80, window.innerHeight - el.offsetHeight - 16));
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    window.addEventListener('resize', fit);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', fit);
    };
  }, [ref]);
  return top;
}

/** A transfer's state as a soft label. */
const STATUS_TONE: Record<TransferStatus, Grad> = {
  waiting: 'butter',
  opened: 'mint',
  cancelled: 'stone',
  expired: 'stone',
};

/** the colour of a grade's level: low coral, then butter, sky, lime */
const levelTone = (points: number): Grad =>
  points <= 3 ? 'coral' : points <= 6 ? 'butter' : points <= 9 ? 'sky' : 'lime';

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="break-inside-avoid">
      <h3
        className="mb-2 font-display text-[22px] leading-7 font-light tracking-[-0.025em]"
        style={{ color: INK }}
      >
        {title}
      </h3>
      {children}
    </section>
  );
}

/** The «шкільний паспорт» as a document: what the new school reads and prints. */
export function PassportView({ passport }: { passport: Passport }) {
  const { t } = useTranslation();
  const p = passport;
  const s = p.student;
  const has = (k: PassportSection) => p.sections.includes(k);
  const none = (
    <p className="text-[14px]" style={{ color: INK_SOFT }}>
      {t('moving.none')}
    </p>
  );
  return (
    <article className="print-area flex flex-col gap-6 rounded-[28px] bg-white p-5 md:p-7">
      <header
        className="flex items-start justify-between gap-4 pb-5"
        style={{ borderBottom: `1px solid ${LINE}` }}
      >
        <div className="min-w-0">
          <p className="text-[13px] font-medium" style={{ color: INK_SOFT }}>
            {t('moving.passport')}
          </p>
          <h2
            className="mt-1 font-display text-[32px] leading-[38px] font-light tracking-[-0.035em] md:text-[38px] md:leading-[44px]"
            style={{ color: INK }}
          >
            {s.lastName} {s.firstName} {s.middleName ?? ''}
          </h2>
        </div>
        <EventArt kind="trip" tone="sky" size={64} />
      </header>
      <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-6 gap-y-1.5 text-[14.5px]">
        {s.birthDate && (
          <>
            <dt style={{ color: INK_SOFT }}>{t('moving.born')}</dt>
            <dd style={{ color: INK }}>{longDate(s.birthDate)}</dd>
          </>
        )}
        <dt style={{ color: INK_SOFT }}>{t('moving.class')}</dt>
        <dd style={{ color: INK }}>{s.class}</dd>
        <dt style={{ color: INK_SOFT }}>{t('moving.school')}</dt>
        <dd style={{ color: INK }}>{s.school}</dd>
        <dt style={{ color: INK_SOFT }}>{t('moving.year')}</dt>
        <dd style={{ color: INK }}>{s.year}</dd>
      </dl>

      {has('grades') && (
        <Section title={t('moving.section.grades')}>
          {(p.grades ?? []).length === 0 ? (
            none
          ) : (
            <div className="-mx-1 overflow-x-auto px-1">
              <table className="w-full min-w-[460px] border-collapse text-[14px]">
                <thead>
                  <tr className="text-left text-[12px]" style={{ color: INK_SOFT }}>
                    <th className="py-2 font-medium">{t('moving.subject')}</th>
                    <th className="py-2 text-center font-medium">{t('moving.average')}</th>
                    {[1, 2].map((n) => (
                      <th key={n} className="py-2 text-center font-medium">
                        {t('moving.semester', { n })}
                      </th>
                    ))}
                    <th className="py-2 text-center font-medium">{t('moving.yearGrade')}</th>
                  </tr>
                </thead>
                <tbody style={{ color: INK }}>
                  {(p.grades ?? []).map((g) => {
                    const look = subjectLook(g.subject);
                    return (
                      <tr key={g.subject} style={{ borderTop: `1px solid ${LINE}` }}>
                        <td className="py-1.5">
                          <span className="flex items-center gap-2.5">
                            <SubjectArt icon={look.icon} tone={SUBJECT_GRAD[look.tone]} size={26} />
                            {g.subject}
                          </span>
                        </td>
                        <td className="py-1.5 text-center">
                          {g.average !== null ? String(g.average).replace('.', ',') : '—'}
                        </td>
                        {[1, 2].map((n) => (
                          <td key={n} className="py-1.5 text-center font-medium">
                            {g.semesters.find((x) => x.term === n)?.value ?? '—'}
                          </td>
                        ))}
                        <td className="py-1.5 text-center font-medium">{g.year ?? '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Section>
      )}

      {has('achievements') && (
        <Section title={t('moving.section.achievements')}>
          {(p.achievements ?? []).length === 0 ? (
            none
          ) : (
            <ul className="flex flex-col">
              {(p.achievements ?? []).map((a) => (
                <li
                  key={`${a.title}-${a.date}`}
                  className="flex items-center gap-3 py-2"
                  style={{ borderTop: `1px solid ${LINE}` }}
                >
                  <KindArt category={a.category as AchievementCategory} size={30} />
                  <span className="min-w-0 flex-1 text-[14.5px]" style={{ color: INK }}>
                    {a.title}
                    <span style={{ color: INK_FAINT }}> · {longDate(a.date)}</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Section>
      )}

      {has('clubs') && (
        <Section title={t('moving.section.clubs')}>
          {(p.clubs ?? []).length === 0 ? (
            none
          ) : (
            <p className="flex flex-wrap gap-1.5">
              {(p.clubs ?? []).map((c, i) => (
                <Label
                  key={c.title}
                  tone={(['mint', 'lavender', 'butter', 'pink'] as const)[i % 4]}
                >
                  {c.title}
                </Label>
              ))}
            </p>
          )}
        </Section>
      )}

      {has('attendance') && p.attendance && (
        <Section title={t('moving.section.attendance')}>
          <div className="grid grid-cols-3 gap-2">
            {(
              [
                [t('moving.lessons'), p.attendance.lessons],
                [t('moving.absent'), p.attendance.absent],
                [t('moving.excused'), p.attendance.excused],
              ] as const
            ).map(([label, value]) => (
              <div key={label} className="rounded-[18px] p-3.5" style={{ backgroundColor: PAPER }}>
                <p
                  className="font-display text-[30px] leading-8 font-light tracking-[-0.03em]"
                  style={{ color: INK }}
                >
                  {value}
                </p>
                <p className="mt-0.5 text-[12.5px] leading-4" style={{ color: INK_SOFT }}>
                  {label}
                </p>
              </div>
            ))}
          </div>
        </Section>
      )}

      {has('yearbook') && p.yearbook && (
        <Section title={t('moving.section.yearbook')}>
          <ul className="flex flex-col gap-1 text-[14.5px]" style={{ color: INK }}>
            <li>
              {t('moving.homeworkDone')}: <span className="font-medium">{p.yearbook.homework}</span>
            </li>
            {p.yearbook.quizzes && p.yearbook.quizzes.count > 0 && (
              <li>
                {t('moving.quizzesDone', {
                  count: p.yearbook.quizzes.count,
                  percent: p.yearbook.quizzes.percent ?? 0,
                })}
              </li>
            )}
            {p.yearbook.puzzles && p.yearbook.puzzles.days > 0 && (
              <li>{t('moving.puzzlesDone', p.yearbook.puzzles)}</li>
            )}
            {p.yearbook.season && (
              <li>
                {t('moving.seasonPart', {
                  title: seasonStory(p.yearbook.season.key)?.title ?? '',
                  parts: p.yearbook.season.parts,
                })}
              </li>
            )}
          </ul>
          {p.yearbook.best.length > 0 && (
            <div className="mt-4">
              <Sub>{t('moving.best')}</Sub>
              <ul className="flex flex-col">
                {p.yearbook.best.map((b) => {
                  const look = subjectLook(b.subject);
                  return (
                    <li
                      key={`${b.subject}-${b.graded_on}-${b.title}`}
                      className="flex items-center gap-3 py-1.5"
                      style={{ borderTop: `1px solid ${LINE}` }}
                    >
                      <SubjectArt icon={look.icon} tone={SUBJECT_GRAD[look.tone]} size={28} />
                      <span className="min-w-0 flex-1 text-[14.5px]" style={{ color: INK }}>
                        {b.subject}
                        {b.title && <span style={{ color: INK_SOFT }}> · {b.title}</span>}
                        <span style={{ color: INK_FAINT }}> · {longDate(b.graded_on)}</span>
                      </span>
                      {/* the grade as a small mark of its level */}
                      <span
                        className="inline-flex h-8 min-w-8 shrink-0 items-center justify-center rounded-[10px] px-1.5 text-[15px] leading-none font-semibold tabular-nums"
                        style={{ backgroundImage: grad(levelTone(b.points)), color: INK }}
                      >
                        {b.points}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </Section>
      )}

      <p
        className="pt-4 text-[12.5px] leading-[18px]"
        style={{ borderTop: `1px solid ${LINE}`, color: INK_SOFT }}
      >
        {t('moving.footer', { date: longDate(p.madeAt) })}
      </p>
    </article>
  );
}

/** A switch: on — the dark track, the knob to the right. */
function Switch({ on, label, onChange }: { on: boolean; label: string; onChange: () => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      onClick={onChange}
      className="relative h-[26px] w-[44px] shrink-0 rounded-full transition-colors duration-200"
      style={{ backgroundColor: on ? 'var(--sh-dark)' : '#E4E0D7' }}
    >
      <span
        className="absolute top-[3px] size-5 rounded-full bg-white transition-[left] duration-300 ease-spring"
        style={{ left: on ? 21 : 3 }}
      />
    </button>
  );
}

/** Print and save: two light pills. */
function Keep({ passport }: { passport: Passport }) {
  const { t } = useTranslation();
  return (
    <>
      <button type="button" onClick={() => window.print()} className={pillClass('light')}>
        {t('moving.print')}
        <PillIcon icon={PrinterIcon} />
      </button>
      <button type="button" onClick={() => download(passport)} className={pillClass('light')}>
        {t('moving.download')}
        <PillIcon icon={DownloadSimpleIcon} />
      </button>
    </>
  );
}

function FamilyMoving({ studentId }: { studentId: string }) {
  const { t } = useTranslation();
  const [sections, setSections] = useState<PassportSection[]>([...PASSPORT_SECTIONS]);
  const preview = usePassportPreview(studentId, sections);
  const transfers = useProfileTransfers({ studentId }).data ?? [];
  const actions = useTransferActions();
  const [made, setMade] = useState<{ code: string; validUntil: string }>();
  const toggle = (k: PassportSection) =>
    setSections(sections.includes(k) ? sections.filter((x) => x !== k) : [...sections, k]);
  // the choice stays in view beside the long passport
  const side = useRef<HTMLDivElement>(null);
  const top = useStickyTop(side);
  return (
    <div className="grid grid-cols-1 items-start gap-4 xl:grid-cols-[440px_minmax(0,1fr)]">
      <div
        ref={side}
        className="flex min-w-0 flex-col gap-4 print:hidden xl:sticky"
        style={{ top }}
      >
        <Block>
          <Head title={t('moving.choose')} />
          <ul className="flex flex-col">
            {PASSPORT_SECTIONS.map((k) => (
              <li
                key={k}
                className="flex items-center gap-4 py-3"
                style={{ borderTop: `1px solid ${LINE}` }}
              >
                <span className="min-w-0 flex-1">
                  <span className="block text-[15px] leading-5 font-medium" style={{ color: INK }}>
                    {t(`moving.section.${k}`)}
                  </span>
                  <span className="block text-[13px] leading-[18px]" style={{ color: INK_SOFT }}>
                    {t(`moving.sectionHint.${k}`)}
                  </span>
                </span>
                <Switch
                  on={sections.includes(k)}
                  label={t(`moving.section.${k}`)}
                  onChange={() => toggle(k)}
                />
              </li>
            ))}
          </ul>
          <p className="mt-2 text-[13px] leading-[18px]" style={{ color: INK_FAINT }}>
            {t('moving.always')}
          </p>
          <div className="mt-5 flex flex-wrap gap-2">
            <button
              type="button"
              disabled={sections.length === 0 || actions.create.isPending}
              onClick={() =>
                void actions.create.mutateAsync({ studentId, sections }).then(
                  (r) => setMade(r),
                  () => undefined,
                )
              }
              className={`${pillClass('dark')} disabled:opacity-35`}
            >
              {t('moving.make')}
              <PillIcon icon={KeyIcon} />
            </button>
            {preview.data && <Keep passport={preview.data} />}
          </div>
        </Block>
        {made && (
          <section
            className="motion-rise relative isolate overflow-hidden rounded-[28px] p-5 md:p-6"
            style={{ backgroundImage: grad('cobalt'), color: GRAD.cobalt.ink }}
          >
            <p className="text-[13px] font-medium">{t('moving.code')}</p>
            <p
              className="mt-2 rounded-[18px] bg-white py-4 text-center font-display text-[34px] leading-10 font-light tracking-[0.08em] select-all md:text-[40px]"
              style={{ color: INK }}
            >
              {formatTransferCode(made.code)}
            </p>
            <p className="mt-3 text-[13.5px] leading-5" style={{ color: INK }}>
              {t('moving.codeHint', { date: longDate(made.validUntil) })}
            </p>
          </section>
        )}
        {transfers.length > 0 && (
          <Block>
            <Head title={t('moving.history')} count={transfers.length} />
            <ul className="flex flex-col">
              {transfers.map((tr) => {
                const status = transferStatus(tr);
                return (
                  <li
                    key={tr.id}
                    className="flex flex-wrap items-center gap-x-3 gap-y-1.5 py-3"
                    style={{ borderTop: `1px solid ${LINE}` }}
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block text-[14.5px]" style={{ color: INK }}>
                        {tr.sections.map((k) => t(`moving.section.${k}`)).join(', ')}
                      </span>
                      <span className="block text-[12.5px]" style={{ color: INK_FAINT }}>
                        {t('moving.made', { date: longDate(tr.createdAt) })}
                        {tr.claimedAt &&
                          ` · ${t('moving.openedAt', { date: longDate(tr.claimedAt) })}`}
                      </span>
                    </span>
                    <Label tone={STATUS_TONE[status]}>{t(`moving.status.${status}`)}</Label>
                    {status === 'waiting' && (
                      <button
                        type="button"
                        onClick={() => actions.revoke.mutate(tr.id)}
                        className={`${pillClass('light', 'sm')} pr-3.5`}
                      >
                        {t('moving.cancel')}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </Block>
        )}
      </div>
      <div className="flex min-w-0 flex-col gap-2">
        <p className="px-1 text-[14px] font-medium print:hidden" style={{ color: INK_SOFT }}>
          {t('moving.preview')}
        </p>
        {preview.isPending && sections.length > 0 ? (
          <div className="h-[480px] animate-pulse rounded-[28px] bg-white" aria-busy />
        ) : (
          preview.data && <PassportView passport={preview.data} />
        )}
      </div>
    </div>
  );
}

function SchoolMoving() {
  const { t } = useTranslation();
  const ctx = useCurrentContext();
  const actions = useTransferActions();
  const transfers = useProfileTransfers({ schoolId: ctx.schoolId }).data ?? [];
  const people = usePeople(ctx.schoolId).data ?? [];
  const nameOf = (id: string) => {
    const p = people.find((x) => x.id === id);
    return p ? `${p.lastName} ${p.firstName}` : '';
  };
  const [code, setCode] = useState('');
  const [opened, setOpened] = useState<Passport>();
  return (
    <div className="grid grid-cols-1 items-start gap-4 xl:grid-cols-[420px_minmax(0,1fr)]">
      <Block className="print:hidden xl:sticky xl:top-20">
        <Head title={t('moving.claimTitle')} />
        <p className="-mt-2 text-[14px] leading-5" style={{ color: INK_SOFT }}>
          {t('moving.claimHint')}
        </p>
        <input
          aria-label={t('moving.claimTitle')}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder={t('moving.claimPlaceholder')}
          maxLength={14}
          className={`mt-4 h-16 w-full px-4 text-center font-display text-[26px] font-light tracking-[0.12em] uppercase ${FIELD}`}
        />
        {actions.claim.isError && (
          <p role="alert" className="mt-2 text-[14px]" style={{ color: GRAD.coral.ink }}>
            {t('moving.claimError')}
          </p>
        )}
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            disabled={code.replace(/[\s-]/g, '').length < 10 || actions.claim.isPending}
            onClick={() =>
              void actions.claim.mutateAsync(code).then(
                (p) => setOpened(p),
                () => undefined,
              )
            }
            className={`${pillClass('dark')} disabled:opacity-35`}
          >
            {t('moving.claim')}
            <PillIcon icon={ArrowUpRightIcon} />
          </button>
          {opened && <Keep passport={opened} />}
        </div>
      </Block>
      <div className="flex min-w-0 flex-col gap-4">
        {opened && <PassportView passport={opened} />}
        <Block className="print:hidden">
          <Head title={t('moving.schoolTitle')} count={transfers.length} />
          {transfers.length === 0 ? (
            <div className="flex flex-col items-center py-8 text-center">
              <EventArt kind="trip" tone="sky" size={80} />
            </div>
          ) : (
            <ul className="flex flex-col">
              {transfers.map((tr) => {
                const status = transferStatus(tr);
                return (
                  <li
                    key={tr.id}
                    className="flex flex-wrap items-center gap-x-3 gap-y-1.5 py-3"
                    style={{ borderTop: `1px solid ${LINE}` }}
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block text-[15px] font-medium" style={{ color: INK }}>
                        {tr.passport.student.lastName} {tr.passport.student.firstName} ·{' '}
                        {tr.passport.student.class}
                      </span>
                      <span className="block text-[12.5px]" style={{ color: INK_SOFT }}>
                        {t('moving.madeBy', { name: nameOf(tr.createdById) })} ·{' '}
                        {longDate(tr.createdAt)} ·{' '}
                        {tr.sections.map((k) => t(`moving.section.${k}`)).join(', ')}
                      </span>
                    </span>
                    <Label tone={STATUS_TONE[status]}>{t(`moving.status.${status}`)}</Label>
                  </li>
                );
              })}
            </ul>
          )}
        </Block>
      </div>
    </div>
  );
}

/** «Переїзд без втрат»: the school life of a child goes with them, with the parent's consent. */
export function MovingPage() {
  const { t } = useTranslation();
  const ctx = useCurrentContext();
  return (
    <div className="flex flex-col gap-4">
      <div className="print:hidden">
        <PageHead
          title={t('moving.title')}
          sub={
            <span className="block max-w-2xl">
              {ctx.kind === 'admin' ? t('moving.schoolHint') : t('moving.intro')}
            </span>
          }
        />
      </div>
      {ctx.kind === 'guardian' ? (
        <FamilyMoving studentId={ctx.child.memberId} />
      ) : ctx.kind === 'admin' ? (
        <SchoolMoving />
      ) : null}
    </div>
  );
}
